/**
 * Utility functions for order processing and display.
 */
export const orderUtils = {
  /**
   * Extracts a short display number from a full order number.
   * Format: MachineCode-Day-Month-Counter (e.g., S-29-4-4)
   * 
   * @param {string} fullNumber - The full order number to format
   * @param {string|Date} createdAt - The creation date of the order
   * @returns {string} The formatted display number
   */
  extractDisplayNumber: (fullNumber, createdAt) => {
    if (!fullNumber || typeof fullNumber !== 'string') return fullNumber;
    
    try {
      const parts = fullNumber.split('-');
      // Expected format: MachineCode-Date-Counter or MachineCode-Counter
      // e.g., S-2904-4 or S-4
      
      if (parts.length >= 2) {
        const machineCode = parts[0];
        const counterPart = parts[parts.length - 1];
        
        // Remove leading zeros from the counter (e.g., 007 -> 7)
        const counter = parseInt(counterPart, 10);
        
        // If parsing fails for any reason, return the original
        if (isNaN(counter)) return fullNumber;
        
        // If createdAt is provided, add date context in S-Day-Month-Counter format
        if (createdAt) {
          const date = new Date(createdAt);
          if (!isNaN(date.getTime())) {
            const day = date.getDate();
            const month = date.getMonth() + 1;
            return `${machineCode}-${day}-${month}-${counter}`;
          }
        }
        
        return `${machineCode}-${counter}`;
      }
      
      return fullNumber;
    } catch (error) {
      console.error('Error formatting order number:', error);
      return fullNumber;
    }
  },

  /**
   * Returns a compact order code like "S-3" or "P-12"
   * e.g., "S-20260817-003" -> "S-3"
   */
  getShortCode: (fullNumber) => {
    if (!fullNumber || typeof fullNumber !== 'string') return fullNumber;
    try {
      const parts = fullNumber.split('-');
      if (parts.length >= 2) {
        const machineCode = parts[0];
        const counterPart = parts[parts.length - 1];
        const counter = parseInt(counterPart, 10);
        if (!isNaN(counter)) {
          return `${machineCode}-${counter}`;
        }
      }
      return fullNumber;
    } catch {
      return fullNumber;
    }
  },

  /**
   * Capitalizes the first letter of every word in a string.
   * e.g., "CHICKEN BURGER" -> "Chicken Burger"
   */
  toTitleCase: (str) => {
    if (!str || typeof str !== 'string') return str;
    return str
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  /**
   * Capitalizes the first letter of a string.
   * e.g., "tilo" -> "Tilo"
   */
  capitalize: (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.trim().charAt(0).toUpperCase() + str.trim().slice(1);
  },

  /**
   * Capitalizes each word in a string.
   */
  capitalizeWords: (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.replace(/\b\w/g, c => c.toUpperCase());
  },

  /**
   * Formats a table/seat identifier matching standard restaurant display conventions:
   * e.g., "4" -> "Table 4" (or "Seat 4" if untracked)
   * e.g., "Table 4" -> "Table 4"
   * e.g., "VIP Lounge" -> "VIP Lounge"
   */
  formatTableDisplayName: (tableNum, isTracked = true, section = '') => {
    if (!tableNum) return 'Dine In';
    const raw = String(tableNum).trim();
    if (!raw) return 'Dine In';

    let display = raw;
    if (/^\d+$/.test(raw)) {
      display = isTracked ? `Table ${raw}` : `Seat ${raw}`;
    }

    if (section && typeof section === 'string' && section.trim() && !display.includes(section.trim())) {
      display = `${display} (${section.trim()})`;
    }
    return display;
  }
};

export default orderUtils;
